import { transliterate } from '../utils/translit';
import { cleanArtistName, cleanTrackName, getArtistVariants } from '../utils/stringCleaners';
import { searchITunes, searchITunesArtist } from './providers/itunesProvider';
import { searchDeezerTrack, searchDeezerArtist } from './providers/deezerProvider';
import { searchLastFMTrack, searchLastFM } from './providers/lastfmProvider';

/**
 * Album Art Service - Main Orchestrator
 * Priority Chain: iTunes -> Deezer -> Last.FM
 * 
 * IMPROVED: Now tries individual artists when collaboration format fails
 * e.g., "WAWA & HERD" -> tries "WAWA & HERD", then "WAWA", then "HERD"
 */

// Cache for album art: key="{cleanArtist}|{cleanTrack}"
const albumArtCache = new Map();

/**
 * Get album art for a track
 * @param {string} originalArtist - Original artist name from stream
 * @param {string} originalTrack - Original track name from stream
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const getAlbumArt = async (originalArtist, originalTrack) => {
    // 0. Basic filtering
    if (!originalArtist || !originalTrack ||
        originalArtist === 'YOUR MUSIC! YOUR STATION!' ||
        originalTrack === 'Загрузка...') {
        return null;
    }

    const cArtist = cleanArtistName(originalArtist);
    const cTrack = cleanTrackName(originalTrack);

    const cacheKey = `${cArtist}|${cTrack}`.toLowerCase();
    if (albumArtCache.has(cacheKey)) return albumArtCache.get(cacheKey);

    console.log(`--- Search: ${cArtist} - ${cTrack} ---`);
    let artUrl = null;

    // Get all artist variants for fallback (e.g., ["WAWA & HERD", "WAWA", "HERD"])
    const artistVariants = getArtistVariants(originalArtist);
    
    // Build search steps: each artist variant + transliterated version
    const trackSteps = [];
    for (const artist of artistVariants) {
        const cleanedArtist = cleanArtistName(artist);
        trackSteps.push({ a: cleanedArtist, t: cTrack, original: cArtist });
        
        const transArtist = transliterate(cleanedArtist);
        if (transArtist !== cleanedArtist) {
            trackSteps.push({ a: transArtist, t: cTrack, original: cArtist });
        }
    }

    // 1. Try iTunes Track search
    for (const step of trackSteps) {
        artUrl = await searchITunes(step.a, step.t, step.original);
        if (artUrl) {
            console.log(`--- Result: FOUND via iTunes ---`);
            albumArtCache.set(cacheKey, artUrl);
            return artUrl;
        }
    }

    // 2. Try Deezer Track search
    for (const step of trackSteps) {
        artUrl = await searchDeezerTrack(step.a, step.t, step.original);
        if (artUrl) {
            console.log(`--- Result: FOUND via Deezer ---`);
            albumArtCache.set(cacheKey, artUrl);
            return artUrl;
        }
    }

    // 3. Try Last.FM Track search
    for (const step of trackSteps) {
        artUrl = await searchLastFMTrack(step.a, step.t);
        if (artUrl) {
            console.log(`--- Result: FOUND via Last.FM Track ---`);
            albumArtCache.set(cacheKey, artUrl);
            return artUrl;
        }
    }

    // 4. Fallbacks to Artist Photo (try each artist variant)
    for (const artist of artistVariants) {
        const cleanedArtist = cleanArtistName(artist);
        
        artUrl = await searchITunesArtist(cleanedArtist);
        if (artUrl) break;
        
        artUrl = await searchDeezerArtist(cleanedArtist);
        if (artUrl) break;
        
        artUrl = await searchLastFM(cleanedArtist);
        if (artUrl) break;
    }

    // Cache result (even if null, to avoid re-searching)
    console.log(`--- Result: ${artUrl ? 'FOUND' : 'NOT FOUND'} ---`);
    albumArtCache.set(cacheKey, artUrl);

    return artUrl;
};

/**
 * Clear the album art cache
 */
export const clearAlbumArtCache = () => {
    albumArtCache.clear();
};

const albumArtService = { getAlbumArt, clearAlbumArtCache };
export default albumArtService;
